import { FormEvent, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { styled } from "@mui/material/styles";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  useMediaQuery,
  Chip,
  Tooltip,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { ArrowBack } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "../../stores";
import {
  getPlanById,
  updatePlanById,
} from "../../features/plan-management-module/planAction";
import { clearPlanMessages } from "../../features/plan-management-module/planSlice";

interface FormData {
  planName: string;
  price: string;
  description: string;
  mode: "public" | "private";
  duration: "monthly" | "yearly" | "custom";
  isPopular: boolean;
  isEmbedded: boolean;
  isWhiteList: boolean;
  isFleetEstimator: boolean;
  isSiteEstimator: boolean;
  features: {
    fleetEstimatorReportLimit: string;
    siteEstimatorReportLimit: string;
    customFeatures: string[];
  };
  newFeature: string;
}

const Container = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "#fff",
  display: "flex",
  flexDirection: "row",
  overflow: "hidden",
  transform: "scale(0.97)",
  transformOrigin: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    transform: "scale(0.95)",
  },
}));

const FormSection = styled(Box)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(1.5),
  width: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
    minHeight: "100vh",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    justifyContent: "flex-start",
  },
}));

const Content = styled(Box)<{ component?: React.ElementType }>(({ theme }) => ({
  width: "100%",
  maxWidth: "auto",
  padding: theme.spacing(1),
  marginTop: theme.spacing(0),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0),
    maxWidth: "100%",
    marginTop: theme.spacing(0),
  },
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  fontSize: "28px",
  fontWeight: 700,
  color: "#455354",
  textAlign: "left",
  [theme.breakpoints.down("sm")]: {
    fontSize: "25px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: "100%",
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    backgroundColor: "#f5f5f5",
    "& fieldset": {
      borderColor: "#e0e0e0",
    },
    "&:hover fieldset": {
      borderColor: "#4b5b5d",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#758d8e",
      borderWidth: "2px",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#757575",
    fontSize: "0.9rem",
    top: "-2px",
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.8rem",
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#758d8e",
    fontWeight: 500,
  },
  "& .MuiOutlinedInput-input": {
    padding: "10px 12px",
    fontSize: "0.85rem",
    [theme.breakpoints.down("sm")]: {
      padding: "7px 10px",
      fontSize: "0.8rem",
    },
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1),
  fontSize: "14px",
  background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
  color: "#f2efe9",
  border: "1px solid #ccc",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.25),
    fontSize: "14px",
  },
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Almarai', sans-serif",
  color: "#455354",
  fontSize: "0.95rem",
  fontWeight: 600,
  marginBottom: theme.spacing(0.5),
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.8rem",
  },
}));

const StyledBackButton = styled(IconButton)(({ theme }) => ({
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "#f2efe9",
  color: "#455354",
  transition: "background 0.24s ease-in-out, color 0.24s ease-in-out",
  "&:hover": {
    background: "linear-gradient(to bottom, #758d8e, #4b5b5d)",
    color: "#f2efe9",
  },
}));

const EditPlan = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();
  const {
    selectedPlan,
    loading: plansLoading,
    error: plansError,
  } = useSelector((state: RootState) => state.planManagement);

  const [formData, setFormData] = useState<FormData>({
    planName: "",
    price: "",
    description: "",
    mode: "public",
    duration: "monthly",
    isPopular: false,
    isEmbedded: false,
    isWhiteList: false,
    isFleetEstimator: false,
    isSiteEstimator: false,
    features: {
      fleetEstimatorReportLimit: "0",
      siteEstimatorReportLimit: "0",
      customFeatures: [],
    },
    newFeature: "",
  });

  const [errors, setErrors] = useState({
    planName: "",
    description: "",
    mode: "",
    duration: "",
    fleetEstimatorReportLimit: "",
    siteEstimatorReportLimit: "",
    estimatorCheckbox: "",
  });

  useEffect(() => {
    if (planId) {
      dispatch(getPlanById(planId));
    }
    if (plansError) {
      dispatch(clearPlanMessages());
    }
  }, [dispatch, planId, plansError]);

  useEffect(() => {
    if (selectedPlan) {
      // Extract custom features (excluding system features)
      const customFeatures = selectedPlan.features.filter(
        (f: string) =>
          f !== "Embed Reports" &&
          f !== "White Label" &&
          f !== "Fleet Estimator" &&
          f !== "Site Estimator" &&
          f !== "Popular Plan" &&
          !f.includes("Fleet Estimator Reports") &&
          !f.includes("Site Estimator Reports")
      );

      // Extract fleet estimator report limit
      let fleetEstimatorReportLimit = "0";
      const fleetReportFeature = selectedPlan.features.find((f: string) =>
        f.includes("Fleet Estimator Reports")
      );
      if (fleetReportFeature) {
        fleetEstimatorReportLimit =
          fleetReportFeature.match(
            /G (\d+) Fleet Estimator Reports/
          )?.[1] || "0";
      }

      // Extract site estimator report limit
      let siteEstimatorReportLimit = "0";
      const siteReportFeature = selectedPlan.features.find((f: string) =>
        f.includes("Site Estimator Reports")
      );
      if (siteReportFeature) {
        siteEstimatorReportLimit =
          siteReportFeature.match(
            / (\d+) Site Estimator Reports/
          )?.[1] || "0";
      }

      // Check if estimators are enabled (either has the basic feature or has report limits)
      const isFleetEstimatorEnabled =
        selectedPlan.isFleetEstimator ||
        selectedPlan.features.includes("Fleet Estimator") ||
        !!fleetReportFeature;

      const isSiteEstimatorEnabled =
        selectedPlan.isSiteEstimator ||
        selectedPlan.features.includes("Site Estimator") ||
        !!siteReportFeature;

      setFormData({
        planName: selectedPlan.planName,
        price: selectedPlan.price.toString(),
        description: selectedPlan.description,
        mode: selectedPlan.mode,
        duration: selectedPlan.duration,
        isPopular: selectedPlan.isPopular || false,
        isEmbedded: selectedPlan.isEmbedded || false,
        isWhiteList: selectedPlan.isWhiteList || false,
        isFleetEstimator: isFleetEstimatorEnabled,
        isSiteEstimator: isSiteEstimatorEnabled,
        features: {
          fleetEstimatorReportLimit: fleetEstimatorReportLimit,
          siteEstimatorReportLimit: siteEstimatorReportLimit,
          customFeatures,
        },
        newFeature: "",
      });
    }
  }, [selectedPlan]);

  const validateField = (name: string, value: string) => {
    switch (name) {
      case "planName":
        return value.trim() ? "" : "Plan Name is required";
      case "description":
        return value.trim() ? "" : "Description is required";
      case "mode":
        return value ? "" : "Plan Type is required";
      case "duration":
        return value ? "" : "Duration is required";
      case "fleetEstimatorReportLimit":
        if (
          formData.isFleetEstimator &&
          value.trim() &&
          parseInt(value) === 0
        ) {
          return "Fleet Estimator Report Limit must be greater than 0 if specified";
        }
        return "";
      case "siteEstimatorReportLimit":
        if (formData.isSiteEstimator && value.trim() && parseInt(value) === 0) {
          return "Site Estimator Report Limit must be greater than 0 if specified";
        }
        return "";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {
      planName: validateField("planName", formData.planName),
      description: validateField("description", formData.description),
      mode: validateField("mode", formData.mode),
      duration: validateField("duration", formData.duration),
      fleetEstimatorReportLimit: validateField(
        "fleetEstimatorReportLimit",
        formData.features.fleetEstimatorReportLimit
      ),
      siteEstimatorReportLimit: validateField(
        "siteEstimatorReportLimit",
        formData.features.siteEstimatorReportLimit
      ),
      estimatorCheckbox:
        !formData.isFleetEstimator && !formData.isSiteEstimator
          ? "At least one estimator (Fleet or Site) must be selected"
          : "",
    };

    setErrors((prev) => ({
      ...prev,
      ...newErrors,
    }));

    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type, checked } = e.target as any;

    // Handle numeric inputs for report limits
    if (
      name === "fleetEstimatorReportLimit" ||
      name === "siteEstimatorReportLimit"
    ) {
      const regex = /^\d*$/;
      if (value === "" || regex.test(value)) {
        setFormData((prev) => ({
          ...prev,
          features: { ...prev.features, [name]: value },
        }));
        setErrors((prev) => ({
          ...prev,
          [name]: validateField(name, value),
        }));
      }
      return;
    }

    if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
      }));

      if (name === "isFleetEstimator" && !checked) {
        setFormData((prev) => ({
          ...prev,
          features: { ...prev.features, fleetEstimatorReportLimit: "0" },
        }));
      }
      if (name === "isSiteEstimator" && !checked) {
        setFormData((prev) => ({
          ...prev,
          features: { ...prev.features, siteEstimatorReportLimit: "0" },
        }));
      }
    } else if (name === "newFeature") {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    } else if (name in formData.features) {
      setFormData((prev) => ({
        ...prev,
        features: { ...prev.features, [name]: value },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleBlur = (
    e: React.FocusEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    if (
      name === "fleetEstimatorReportLimit" ||
      name === "siteEstimatorReportLimit"
    ) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleAddFeature = () => {
    if (
      formData.newFeature.trim() &&
      !formData.features.customFeatures.includes(formData.newFeature.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        features: {
          ...prev.features,
          customFeatures: [
            ...prev.features.customFeatures,
            formData.newFeature.trim(),
          ],
        },
        newFeature: "",
      }));
    }
  };

  const handleRemoveCustomFeature = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        customFeatures: prev.features.customFeatures.filter(
          (_, i) => i !== index
        ),
      },
    }));
  };

  const handleEditSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const featuresList: string[] = [];

    // Add fleet estimator feature if enabled
    if (formData.isFleetEstimator) {
      if (parseInt(formData.features.fleetEstimatorReportLimit) > 0) {
        featuresList.push(
          `${formData.features.fleetEstimatorReportLimit} Fleet Estimator Reports`
        );
      } else {
        featuresList.push("Fleet Estimator");
      }
    }

    // Add site estimator feature if enabled
    if (formData.isSiteEstimator) {
      if (parseInt(formData.features.siteEstimatorReportLimit) > 0) {
        featuresList.push(
          `${formData.features.siteEstimatorReportLimit} Site Estimator Reports`
        );
      } else {
        featuresList.push("Site Estimator");
      }
    }

    if (formData.isEmbedded) {
      featuresList.push("Embed Reports");
    }
    if (formData.isWhiteList) {
      featuresList.push("White Label");
    }
    if (formData.isPopular) {
      featuresList.push("Popular Plan");
    }

    featuresList.push(...formData.features.customFeatures);

    const planData = {
      planName: formData.planName,
      price: parseFloat(formData.price) || 0,
      description: formData.description,
      duration: formData.duration,
      features: featuresList,
      fleetEstimatorReportLimit: formData.isFleetEstimator
        ? parseInt(formData.features.fleetEstimatorReportLimit) || 0
        : 0,
      siteEstimatorReportLimit: formData.isSiteEstimator
        ? parseInt(formData.features.siteEstimatorReportLimit) || 0
        : 0,
      isPopular: formData.isPopular,
      isEmbedded: formData.isEmbedded,
      isWhiteList: formData.isWhiteList,
      isFleetEstimator: formData.isFleetEstimator,
      isSiteEstimator: formData.isSiteEstimator,
      mode: formData.mode,
    };

    if (planId) {
      const result = await dispatch(
        updatePlanById({ id: planId, data: planData })
      );
      if (updatePlanById.fulfilled.match(result)) {
        navigate("/main/subscriptions");
      }
    }
  };

  const handleCancel = () => {
    navigate("/main/subscriptions");
  };

  const selectMenuProps = {
    PaperProps: {
      style: {
        maxHeight: 250,
        maxWidth: 200,
        overflowY: "auto" as const,
        backgroundColor: "#fff",
        marginTop: 5,
        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
        borderRadius: "7px",
        border: "1px solid #e0e0e0",
      },
    },
  };

  return (
    <Container>
      <FormSection>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Content component="form">
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <WelcomeText>Edit Plan</WelcomeText>
              <Tooltip title="Back">
                <StyledBackButton
                  aria-label="back"
                  onClick={() => navigate("/main/subscriptions")}
                >
                  <ArrowBack />
                </StyledBackButton>
              </Tooltip>
            </Box>
            <Box
              sx={{
                background: "linear-gradient(135deg, #edefee 0%, #fcfbf9 100%)",
                padding: isMobile ? 2 : 2.5,
                borderRadius: 2,
              }}
            >
              {plansLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : selectedPlan ? (
                <FormSection>
                  <Typography
                    sx={{
                      color: "#4b5b5d",
                      fontSize: 24,
                      fontWeight: 600,
                      fontFamily: "'Almarai', sans-serif",
                      mb: 2,
                    }}
                  >
                    Edit Plan
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Plan Name</FieldLabel>
                      <StyledTextField
                        name="planName"
                        variant="outlined"
                        value={formData.planName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.planName}
                        helperText={errors.planName}
                        required
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Price</FieldLabel>
                      <StyledTextField
                        name="price"
                        variant="outlined"
                        value={formData.price}
                        onChange={handleChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              $/price
                            </InputAdornment>
                          ),
                        }}
                        inputProps={{
                          inputMode: "numeric",
                          pattern: "[0-9]*\\.?[0-9]*",
                        }}
                        disabled
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Plan Type</FieldLabel>
                      <StyledTextField
                        select
                        name="mode"
                        variant="outlined"
                        value={formData.mode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.mode}
                        helperText={errors.mode}
                        required
                        disabled
                        SelectProps={{
                          MenuProps: selectMenuProps,
                          renderValue: (value: unknown) => (
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#4b5b5d",
                              }}
                            >
                              {(value as string) === "public"
                                ? "Public"
                                : "Private"}
                            </Typography>
                          ),
                        }}
                      >
                        <MenuItem
                          value="public"
                          sx={{
                            fontSize: "0.85rem",
                            padding: "6px 12px",
                            "&:hover": {
                              backgroundColor: "#f5f0e7",
                            },
                          }}
                        >
                          Public
                        </MenuItem>
                        <MenuItem
                          value="private"
                          sx={{
                            fontSize: "0.85rem",
                            padding: "6px 12px",
                            "&:hover": {
                              backgroundColor: "#f5f0e7",
                            },
                          }}
                        >
                          Private
                        </MenuItem>
                      </StyledTextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FieldLabel>Duration</FieldLabel>
                      <StyledTextField
                        select
                        name="duration"
                        variant="outlined"
                        value={formData.duration}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!errors.duration}
                        helperText={errors.duration}
                        required
                        disabled
                        SelectProps={{
                          MenuProps: selectMenuProps,
                          renderValue: (value: unknown) => (
                            <Typography
                              sx={{
                                fontSize: "0.85rem",
                                color: "#4b5b5d",
                              }}
                            >
                              {(value as string) === "monthly"
                                ? "Monthly"
                                : (value as string) === "yearly"
                                ? "Yearly"
                                : "Custom"}
                            </Typography>
                          ),
                        }}
                      >
                        <MenuItem
                          value="monthly"
                          sx={{
                            fontSize: "0.85rem",
                            padding: "6px 12px",
                            "&:hover": {
                              backgroundColor: "#f5f0e7",
                            },
                          }}
                        >
                          Monthly
                        </MenuItem>
                        <MenuItem
                          value="yearly"
                          sx={{
                            fontSize: "0.85rem",
                            padding: "6px 12px",
                            "&:hover": {
                              backgroundColor: "#f5f0e7",
                            },
                          }}
                        >
                          Yearly
                        </MenuItem>
                        <MenuItem
                          value="custom"
                          sx={{
                            fontSize: "0.85rem",
                            padding: "6px 12px",
                            "&:hover": {
                              backgroundColor: "#f5f0e7",
                            },
                          }}
                        >
                          Custom
                        </MenuItem>
                      </StyledTextField>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <FieldLabel>Description</FieldLabel>
                      <StyledTextField
                        name="description"
                        variant="outlined"
                        value={formData.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        rows={2}
                        placeholder="What this plan includes..."
                        error={!!errors.description}
                        helperText={errors.description}
                        required
                      />
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <FieldLabel>Additional Features</FieldLabel>
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isPopular"
                              checked={formData.isPopular}
                              onChange={handleChange}
                              size="small"
                              sx={{
                                ml: 1,
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                            />
                          }
                          label="Is Popular"
                          sx={{
                            color: "#4b5b5d",
                            mr: 2,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isEmbedded"
                              checked={formData.isEmbedded}
                              onChange={handleChange}
                              size="small"
                              sx={{
                                ml: 1,
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                            />
                          }
                          label="Embed Reports"
                          sx={{
                            color: "#4b5b5d",
                            mr: 2,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isWhiteList"
                              checked={formData.isWhiteList}
                              onChange={handleChange}
                              size="small"
                              sx={{
                                ml: 1,
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                            />
                          }
                          label="White Label"
                          sx={{
                            color: "#4b5b5d",
                            mr: 2,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isFleetEstimator"
                              checked={formData.isFleetEstimator}
                              onChange={handleChange}
                              size="small"
                              sx={{
                                ml: 1,
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                            />
                          }
                          label="Fleet Estimator"
                          sx={{
                            color: "#4b5b5d",
                            mr: 2,
                          }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              name="isSiteEstimator"
                              checked={formData.isSiteEstimator}
                              onChange={handleChange}
                              size="small"
                              sx={{
                                ml: 1,
                                color: "#4b5b5d",
                                "&.Mui-checked": {
                                  color: "#4b5b5d",
                                },
                              }}
                            />
                          }
                          label="Site Estimator"
                          sx={{
                            color: "#4b5b5d",
                            mr: 2,
                          }}
                        />
                        {errors.estimatorCheckbox && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ display: "block", mt: 1 }}
                          >
                            {errors.estimatorCheckbox}
                          </Typography>
                        )}
                      </Grid>

                      {/* Fleet Estimator Report Limit */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FieldLabel>Fleet Estimator Report Limit</FieldLabel>
                        <StyledTextField
                          name="fleetEstimatorReportLimit"
                          variant="outlined"
                          value={formData.features.fleetEstimatorReportLimit}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.fleetEstimatorReportLimit}
                          helperText={errors.fleetEstimatorReportLimit}
                          disabled={!formData.isFleetEstimator}
                          required={formData.isFleetEstimator}
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                        />
                      </Grid>

                      {/* Site Estimator Report Limit */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FieldLabel>Site Estimator Report Limit</FieldLabel>
                        <StyledTextField
                          name="siteEstimatorReportLimit"
                          variant="outlined"
                          value={formData.features.siteEstimatorReportLimit}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={!!errors.siteEstimatorReportLimit}
                          helperText={errors.siteEstimatorReportLimit}
                          disabled={!formData.isSiteEstimator}
                          required={formData.isSiteEstimator}
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{
                      color: "#4b5b5d",
                      fontSize: 24,
                      fontWeight: 600,
                      fontFamily: "'Almarai', sans-serif",
                      mb: 2,
                      mt: 1,
                    }}
                  >
                    Plan Builder
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12 }}>
                      <FieldLabel>Add New Features</FieldLabel>
                      <StyledTextField
                        name="newFeature"
                        variant="outlined"
                        value={formData.newFeature}
                        onChange={handleChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Button
                                variant="contained"
                                onClick={handleAddFeature}
                                sx={{
                                  backgroundColor: "#4b5b5d",
                                  color: "#fff",
                                  "&:hover": {
                                    backgroundColor: "#354142",
                                  },
                                  minWidth: "auto",
                                  padding: theme.spacing(0.1, 0.5),
                                }}
                              >
                                <AddIcon />
                              </Button>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Typography
                    sx={{
                      color: "#4b5b5d",
                      fontSize: 24,
                      fontWeight: 600,
                      fontFamily: "'Almarai', sans-serif",
                      mb: 2,
                      mt: 1,
                    }}
                  >
                    Added Features Preview
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      mb: 2,
                      p: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {formData.features.customFeatures.map((feature, index) => (
                      <Chip
                        key={index}
                        label={feature}
                        onDelete={() => handleRemoveCustomFeature(index)}
                        sx={{
                          background:
                            "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                          color: "#4b5b5d",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                    {formData.isFleetEstimator &&
                      parseInt(formData.features.fleetEstimatorReportLimit) >
                        0 && (
                        <Chip
                          label={`${formData.features.fleetEstimatorReportLimit} Fleet Estimator Reports`}
                          onDelete={() => {
                            setFormData((prev) => ({
                              ...prev,
                              isFleetEstimator: false,
                              features: {
                                ...prev.features,
                                fleetEstimatorReportLimit: "0",
                              },
                            }));
                          }}
                          sx={{
                            background:
                              "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                            color: "#4b5b5d",
                            fontWeight: 500,
                          }}
                        />
                      )}

                    {formData.isSiteEstimator &&
                      parseInt(formData.features.siteEstimatorReportLimit) >
                        0 && (
                        <Chip
                          label={`${formData.features.siteEstimatorReportLimit} Site Estimator Reports`}
                          onDelete={() => {
                            setFormData((prev) => ({
                              ...prev,
                              isSiteEstimator: false,
                              features: {
                                ...prev.features,
                                siteEstimatorReportLimit: "0",
                              },
                            }));
                          }}
                          sx={{
                            background:
                              "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                            color: "#4b5b5d",
                            fontWeight: 500,
                          }}
                        />
                      )}

                    {/* Fleet Estimator without Report Limit */}
                    {formData.isFleetEstimator &&
                      parseInt(formData.features.fleetEstimatorReportLimit) ===
                        0 && (
                        <Chip
                          label="Fleet Estimator"
                          onDelete={() => {
                            setFormData((prev) => ({
                              ...prev,
                              isFleetEstimator: false,
                            }));
                          }}
                          sx={{
                            background:
                              "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                            color: "#4b5b5d",
                            fontWeight: 500,
                          }}
                        />
                      )}

                    {/* Site Estimator without Report Limit */}
                    {formData.isSiteEstimator &&
                      parseInt(formData.features.siteEstimatorReportLimit) ===
                        0 && (
                        <Chip
                          label="Site Estimator"
                          onDelete={() => {
                            setFormData((prev) => ({
                              ...prev,
                              isSiteEstimator: false,
                            }));
                          }}
                          sx={{
                            background:
                              "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                            color: "#4b5b5d",
                            fontWeight: 500,
                          }}
                        />
                      )}

                    {formData.isEmbedded && (
                      <Chip
                        label="Embed Reports"
                        onDelete={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isEmbedded: false,
                          }))
                        }
                        sx={{
                          background:
                            "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                          color: "#4b5b5d",
                          fontWeight: 500,
                        }}
                      />
                    )}
                    {formData.isWhiteList && (
                      <Chip
                        label="White Label"
                        onDelete={() =>
                          setFormData((prev) => ({
                            ...prev,
                            isWhiteList: false,
                          }))
                        }
                        sx={{
                          background:
                            "linear-gradient(to bottom, #F5F0E7, #CDC8BE)",
                          color: "#4b5b5d",
                          fontWeight: 500,
                        }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 2,
                      mt: 2,
                      width: "100%",
                    }}
                  >
                    <AddButton
                      type="submit"
                      disabled={plansLoading}
                      onClick={handleEditSubmit}
                    >
                      {plansLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Update Plan"
                      )}
                    </AddButton>
                    <Button
                      variant="outlined"
                      sx={{
                        textTransform: "none",
                        borderColor: "#ccc",
                        color: "#4b5b5d",
                      }}
                      onClick={handleCancel}
                      disabled={plansLoading}
                    >
                      Cancel
                    </Button>
                  </Box>
                </FormSection>
              ) : (
                <Typography
                  sx={{
                    color: "#4b5b5d",
                    fontSize: 18,
                    fontWeight: 500,
                    textAlign: "center",
                  }}
                >
                  Plan not found
                </Typography>
              )}
            </Box>
          </Content>
        </motion.div>
      </FormSection>
    </Container>
  );
};

export default EditPlan;
